/**
 * DEN AUFTRAG PER MAIL WEITERGEBEN, DIE EMPFÄNGER AUS DEM ADRESSBUCH (.595, NE3 und NE4).
 *
 * ORIGINALTEXT MARVIN (02.11.2026)
 *   „wenn du auf den E-Mail-Button klickst, noch das Adressbuch der Telekom oder
 *    allgemein das Adressbuch davor ... wir wählen dann aus dem Adressbuch die
 *    Empfänger aus ... dann werden die automatisch ins Outlook mit übergeben als
 *    Empfänger. Kannst dann auch auswählen, ob sie an Copy oder Blind Copy
 *    ausgewählt werden sollen ... Wobei Blind Copy eigentlich nie gebraucht
 *    wird. So, und dann brauchen wir nämlich nur noch den Text schreiben."
 *
 * Das Fenster zeigt das Adressbuch des Bereichs (Einträge und Ansprechpartner
 * mit Mailadresse), mit Suche. Je Adresse: An, Cc oder (selten) Bcc. „Im
 * Mailprogramm öffnen" baut daraus einen mailto-Link, der Betreff steht schon.
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api.ts';
import { Dialog, Fehlerkasten, ausFehler, type Fehlerlage } from './Formular.tsx';
import { Suchfeld, passtSuche } from './Schluesselsuche.tsx';

type Feld = 'an' | 'cc' | 'bcc';
type Adresse = { email: string; name: string; wo: string; gruppe: string };

export function MailMitAdressbuch({ bereich, betreff, zu }: { bereich: 'ne3' | 'ne4'; betreff: string; zu: () => void }) {
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [suche, setSuche] = useState('');
  const [wahl, setWahl] = useState<Record<string, Feld>>({});
  const [frei, setFrei] = useState('');
  const [mitBcc, setMitBcc] = useState(false);
  /* .598: NACH GRUPPE WÄHLEN. Marvin, 02.11.2026: „hier muss ich auch die Möglichkeit haben, oben nach Gruppen zu
     selektieren ... Adressbuch öffnen, Gruppe auswählen und dann kann ich die Leute entsprechend anklicken." */
  const [gruppe, setGruppe] = useState('');
  useEffect(() => { api.adressbuch(bereich).then(setD).catch((f) => setFehler(ausFehler(f))); }, [bereich]);

  /* Alle Adressen mit Mail: eigene Einträge, ihre Ansprechpartner und die aus der Plattform. */
  const adressen = useMemo<Adresse[]>(() => {
    if (!d) return [];
    const gruppe = (id: string | null) => (d.gruppen ?? []).find((g: any) => g.id === id)?.name ?? '';
    const raus: Adresse[] = [];
    const nimm = (k: any, wo: string) => {
      if (k.email) raus.push({ email: k.email, name: k.name ?? k.email, wo: [k.firma && k.firma !== k.name ? k.firma : null, wo].filter(Boolean).join(', '), gruppe: wo });
      for (const x of k.ansprechpartner ?? []) if (x.email) raus.push({ email: x.email, name: x.name ?? x.email, wo: [k.name, x.funktion, wo].filter(Boolean).join(', '), gruppe: wo });
    };
    for (const k of d.kontakte ?? []) nimm(k, gruppe(k.gruppe_id));
    for (const p of d.plattform ?? []) for (const k of p.kontakte ?? []) nimm(k, p.name);
    const gesehen = new Set<string>();
    return raus.filter((a) => { const s = a.email.toLowerCase(); if (gesehen.has(s)) return false; gesehen.add(s); return true; })
      .sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }, [d]);

  const gruppen = [...new Set(adressen.map((a) => a.gruppe).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'de'));
  const sicht = adressen.filter((a) => (!gruppe || a.gruppe === gruppe)).filter((a) => suche.trim().split(/\s+/).filter(Boolean).every((w) => passtSuche(w, a.name, a.email, a.wo)));
  const freieListe = frei.split(/[;,\s]+/).map((x) => x.trim()).filter((x) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(x));
  const liste = (f: Feld) => [...Object.entries(wahl).filter(([, v]) => v === f).map(([k]) => k), ...(f === 'an' ? freieListe : [])];
  const an = liste('an'), cc = liste('cc'), bcc = liste('bcc');
  const setze = (email: string, f: Feld | null) => setWahl((alt) => { const n = { ...alt }; if (f && n[email] !== f) n[email] = f; else delete n[email]; return n; });

  function oeffnen() {
    const teile = [cc.length ? `cc=${encodeURIComponent(cc.join(','))}` : null, bcc.length ? `bcc=${encodeURIComponent(bcc.join(','))}` : null, `subject=${encodeURIComponent(betreff)}`].filter(Boolean);
    window.location.href = `mailto:${an.map(encodeURIComponent).join(',')}?${teile.join('&')}`;
    zu();
  }

  return (
    <Dialog titel="Auftrag per E-Mail" untertitel={betreff} breite={820} zu={zu}>
      <Fehlerkasten lage={fehler} />
      <div className="reihe" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 8 }}>
        <div className="feld" style={{ margin: 0, width: 240 }}><label>Gruppe</label>
          <select value={gruppe} onChange={(e) => setGruppe(e.target.value)} style={{ width: '100%' }}>
            <option value="">alle</option>
            {gruppen.map((g) => <option key={g} value={g}>{g} ({adressen.filter((a) => a.gruppe === g).length})</option>)}
          </select>
        </div>
        <div className="feld" style={{ margin: 0, width: 300 }}><label>Im Adressbuch suchen</label><Suchfeld wert={suche} setze={setSuche} mehr="Name, Firma, Mail" breit={300} /></div>
        <label className="wahlzeile klein" style={{ gap: 6 }}><input type="checkbox" checked={mitBcc} onChange={(e) => setMitBcc(e.target.checked)} /> Bcc anbieten</label>
      </div>
      {!d && !fehler && <div className="klein leise">Adressbuch wird geladen.</div>}
      {d && !adressen.length && <div className="klein leise">Im Adressbuch steht noch niemand mit Mailadresse.</div>}
      {!!sicht.length && (
        <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid var(--rand)', borderRadius: 8 }}>
          <table className="liste"><tbody>
            {sicht.map((a) => {
              const f = wahl[a.email];
              return (
                <tr key={a.email} style={{ cursor: 'default' }} className={f ? 'gewaehlt' : undefined}>
                  <td><b>{a.name}</b><div className="klein leise">{a.email}{a.wo ? `, ${a.wo}` : ''}</div></td>
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {(['an', 'cc', ...(mitBcc ? ['bcc'] as Feld[] : [])] as Feld[]).map((x) => (
                      <button key={x} type="button" className={'knopf klein' + (f === x ? ' haupt' : '')} style={{ marginLeft: 4 }} onClick={() => setze(a.email, x)}>{x === 'an' ? 'An' : x === 'cc' ? 'Cc' : 'Bcc'}</button>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody></table>
        </div>
      )}
      <div className="feld" style={{ marginTop: 10 }}>
        <label>Weitere Adresse (nicht im Adressbuch)</label>
        <input value={frei} onChange={(e) => setFrei(e.target.value)} placeholder="name@beispiel.de; zweite@beispiel.de" style={{ width: '100%' }} />
      </div>
      <div className="klein" style={{ marginTop: 6 }}>
        <div><b>An:</b> {an.join(', ') || <span className="leise">niemand</span>}</div>
        <div><b>Cc:</b> {cc.join(', ') || <span className="leise">niemand</span>}</div>
        {bcc.length > 0 && <div><b>Bcc:</b> {bcc.join(', ')}</div>}
      </div>
      <div className="reihe" style={{ marginTop: 12, gap: 8 }}>
        <button type="button" className="knopf" onClick={zu}>Abbrechen</button>
        <button type="button" className="knopf haupt" onClick={oeffnen}>Im Mailprogramm öffnen</button>
      </div>
      <p className="klein leise">Das Mailprogramm öffnet sich mit Empfängern und Betreff; den Text schreibst du dort.</p>
    </Dialog>
  );
}
